import { Bot } from 'lucide-react';
import { formatCurrencyUsd, formatTokenCount } from './usageFormat';
import type { OrgAutomationUsage } from '@/services/org-admin';

/**
 * Org spend that belongs to no seat — batch pipelines and anything else running without a live user.
 *
 * ## Why this is its own line and not a row in the members table
 *
 * It has no member. Putting it in that table would give it a fake identity, break the per-member
 * sort (it is not a person to rank), and drag it into the "who is spending it" chart, which answers
 * a question about people. It is a different KIND of spend, so it gets its own line.
 *
 * ## Why it carries no budget meter
 *
 * The meter measures a seat against its budget. Automation seats are deliberately uncapped — they
 * have no budget to be a proportion of — so a meter here would either invent a denominator or
 * render an empty track that reads as "0% used". The absence is stated in words instead.
 *
 * This spend was invisible in every report until the backend learned to look for customers under
 * the org prefix that match no member; in dev that was 87% of all spend.
 */

export interface AutomationUsageRowProps {
  automation: OrgAutomationUsage;
  /** Names the window the figure covers, so it is never read as all-time. */
  scopeLabel: string;
}

export function AutomationUsageRow({ automation, scopeLabel }: AutomationUsageRowProps) {
  // Nothing to show for an org that runs no automation — and an empty panel claiming $0.00 would
  // imply a measurement where there is simply no such workload.
  if (!automation.seats.length) return null;

  const tokens = automation.prompt_tokens + automation.completion_tokens;

  return (
    <section className="oa-panel" aria-labelledby="oa-automation-title">
      <header className="oa-toolbar flex flex-wrap items-start justify-between gap-x-3 gap-y-1 py-2.5">
        <div className="min-w-0">
          <h3 id="oa-automation-title" className="oa-title flex items-center gap-1.5">
            <Bot className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            Automation
          </h3>
          <p className="oa-meta mt-0.5">
            Work run without a signed-in member — batch pipelines and scheduled jobs. Not capped by
            a seat budget, so it is counted separately from the members above.
          </p>
        </div>
        <dl className="flex shrink-0 items-baseline gap-6">
          <div className="text-right">
            <dt className="oa-micro">Tokens</dt>
            <dd className="mt-0.5 text-[15px] font-semibold tabular-nums">
              {formatTokenCount(tokens)}
            </dd>
          </div>
          <div className="text-right">
            <dt className="oa-micro">Spend</dt>
            <dd className="mt-0.5 text-[15px] font-semibold tabular-nums">
              {formatCurrencyUsd(automation.spend_usd_window)}
            </dd>
          </div>
        </dl>
      </header>

      {/* Per seat, because "pipeline" is one convention and a second service could add another —
          naming them is how an unexpected one gets noticed rather than silently absorbed. */}
      <table className="oa-table">
        <caption className="sr-only">Automation spend {scopeLabel}</caption>
        <thead>
          <tr>
            <th scope="col">Source</th>
            <th scope="col" className="oa-num">
              Tokens
            </th>
            <th scope="col" className="oa-num">
              Spend
            </th>
          </tr>
        </thead>
        <tbody>
          {automation.seats.map((seat) => (
            <tr key={seat.customer_id}>
              <td className="truncate font-medium">{seat.name}</td>
              <td className="oa-num">
                {formatTokenCount(seat.prompt_tokens + seat.completion_tokens)}
              </td>
              <td className="oa-num">{formatCurrencyUsd(seat.spend_usd_window)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
